/**
 * Centralized Date Utilities
 *
 * This is the SINGLE SOURCE for all date parsing and formatting.
 * Uses date-fns for reliable timezone-safe operations.
 */

import { format } from 'date-fns';

/**
 * Parse a date value (string, Date, or number) into a local Date object.
 * Handles ISO timestamps without UTC->local timezone shift.
 */
export function parseDateToLocal(date: string | Date | number | undefined | null): Date | null {
  if (!date) return null;

  if (date instanceof Date) {
    return date;
  }

  if (typeof date === 'number') {
    return new Date(date);
  }

  // String handling - avoid UTC timezone shift
  if (typeof date === 'string') {
    // YYYY-MM-DD format - parse as local date
    const isoMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, year = '', month = '', day = ''] = isoMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // ISO timestamp like "2025-12-24T00:00:00.000Z" - extract date part
    if (date.includes('T')) {
      const datePart = date.substring(0, 10);
      // Missing parts parse as NaN either way ('' and undefined both yield NaN).
      const [year = '', month = '', day = ''] = datePart.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // MM/DD/YYYY format
    if (date.includes('/')) {
      const parts = date.split('/');
      const [first, second, third] = parts;
      if (parts.length === 3 && first !== undefined && second !== undefined && third !== undefined) {
        return new Date(parseInt(third), parseInt(first) - 1, parseInt(second));
      }
    }

    // Fallback to native parsing
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

/**
 * Sort key (ms) for a date-only value, read as a local calendar date like
 * `parseDateToLocal`; 0 when missing or unparseable so comparators never see
 * NaN. Use it instead of `new Date(x.date).getTime()` in sort comparators.
 */
export function dateSortKey(date: string | Date | number | undefined | null): number {
  const time = parseDateToLocal(date)?.getTime();
  return time === undefined || Number.isNaN(time) ? 0 : time;
}

/**
 * Format a Date object to YYYY-MM-DD string.
 * Preserves local timezone (no UTC conversion).
 */
export function formatDateYMD(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Format a Date or string to YYYY-MM-DD string.
 * Handles ISO timestamps without timezone shift.
 */
export function toDateString(date: Date | string): string {
  const parsed = parseDateToLocal(date);
  if (!parsed) return '';
  return formatDateYMD(parsed);
}

/**
 * Format a date for human-readable display.
 * @param date - Date value to format
 * @param style - 'short' (12/24/2025), 'medium' (Dec 24, 2025), 'long' (December 24, 2025), 'compact' (Dec 24)
 */
export function formatDateDisplay(
  date: string | Date | number | undefined | null,
  style: 'short' | 'medium' | 'long' | 'compact' = 'short'
): string {
  const parsed = parseDateToLocal(date);
  if (!parsed) return '-';

  switch (style) {
    case 'short':
      return format(parsed, 'MM/dd/yyyy');
    case 'medium':
      return format(parsed, 'MMM d, yyyy');
    case 'long':
      return format(parsed, 'MMMM d, yyyy');
    case 'compact':
      return format(parsed, 'MMM d');
  }
}

/**
 * Format a timestamp for display in the user's local timezone.
 * Unlike formatDateDisplay (which extracts date-only from UTC timestamps),
 * this function properly converts the timestamp to local timezone first.
 *
 * Use this for system timestamps like createdAt, updatedAt, lastSyncAt, etc.
 * Use formatDateDisplay for transaction dates (which are date-only values).
 *
 * @param timestamp - ISO timestamp string, Date object, or number
 * @param style - 'short' (12/24/2025), 'medium' (Dec 24, 2025), 'long' (December 24, 2025),
 *                'datetime' (12/24/2025, 3:45 PM), 'time' (3:45 PM)
 */
export function formatTimestamp(
  timestamp: string | Date | number | undefined | null,
  style: 'short' | 'medium' | 'long' | 'datetime' | 'time' = 'short'
): string {
  if (!timestamp) return '-';

  // Parse as a proper timestamp (respects timezone)
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return '-';

  switch (style) {
    case 'short':
      return format(date, 'MM/dd/yyyy');
    case 'medium':
      return format(date, 'MMM d, yyyy');
    case 'long':
      return format(date, 'MMMM d, yyyy');
    case 'datetime':
      return format(date, 'MM/dd/yyyy, h:mm a');
    case 'time':
      return format(date, 'h:mm a');
  }
}

/**
 * Extract year from date string or Date object
 * Handles date strings by parsing the year directly to avoid timezone issues
 *
 * @param date - Date string (YYYY-MM-DD) or Date object
 * @returns Year as number
 */
export function getYearFromDate(date: string | Date): number {
  if (typeof date === 'string') {
    // Parse year directly from string to avoid timezone issues
    // split() always yields at least one element; '' parses to NaN like before.
    return parseInt(date.split('-')[0] ?? '', 10);
  }
  return date.getFullYear();
}

/**
 * Calculate quarter from date
 * Returns quarter in format "YYYY-Q#" (e.g., "2024-Q3")
 *
 * @param date - Date string (YYYY-MM-DD) or Date object
 * @returns Quarter string in format "YYYY-Q#"
 */
export function getQuarter(date: string | Date): string {
  const parsed = parseDateToLocal(date);
  if (!parsed) return '';

  const year = parsed.getFullYear();
  const quarter = Math.ceil((parsed.getMonth() + 1) / 3);
  return `${year}-Q${quarter}`;
}

/**
 * Calculate simple quarter number (1-4) from date
 * Returns quarter in format "Q#" (e.g., "Q3")
 *
 * @param date - Date string (YYYY-MM-DD) or Date object
 * @returns Quarter string in format "Q#"
 */
export function getQuarterNumber(date: string | Date): string {
  const parsed = parseDateToLocal(date);
  if (!parsed) return '';

  return `Q${Math.ceil((parsed.getMonth() + 1) / 3)}`;
}

/**
 * The calendar quarter ("Q1"–"Q4") of `today` in local time.
 */
export function getCurrentQuarter(today: Date = new Date()): 'Q1' | 'Q2' | 'Q3' | 'Q4' {
  const month = today.getMonth();
  if (month < 3) return 'Q1';
  if (month < 6) return 'Q2';
  if (month < 9) return 'Q3';
  return 'Q4';
}
